import { useEffect, useRef, useState, type FormEvent } from 'react'
import { useMutation, useQuery } from 'convex/react'
import { ConvexError } from 'convex/values'
import type { FunctionReturnType } from 'convex/server'
import { Archive, ArchiveRestore, CheckCircle2, Loader2, Pencil, Plus, Search } from 'lucide-react'
import { api } from '../../../convex/_generated/api'
import type { Id } from '../../../convex/_generated/dataModel'
import { useAuth, usePermissions } from '@/hooks/useAuth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ErrorBoundary } from '@/components/ui/ErrorBoundary'
import { formatCurrency } from '@/lib/utils'

type Laboratory = FunctionReturnType<typeof api.externalLabs.listLaboratories>[number]
type PriceItem = FunctionReturnType<typeof api.externalLabs.listPriceItems>[number]
type Feedback = { kind: 'success' | 'error'; message: string }
type ArchiveTarget = { kind: 'laboratory'; item: Laboratory } | { kind: 'price'; item: PriceItem }
type ClinicOption = { _id: Id<'companies'>; nome: string }

const fieldClass = 'w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50'

function errorMessage(error: unknown): string {
  if (error instanceof ConvexError && typeof error.data === 'string') return error.data
  return 'Non è stato possibile salvare. Controlla la connessione e riprova: i dati inseriti sono ancora disponibili.'
}

function FeedbackMessage({ feedback }: { feedback: Feedback | null }) {
  if (!feedback) return null
  return (
    <p
      role={feedback.kind === 'error' ? 'alert' : 'status'}
      className={`flex items-start gap-2 rounded-md border p-3 text-sm ${feedback.kind === 'error' ? 'border-destructive/30 bg-destructive/5 text-destructive' : 'border-border bg-muted/40 text-foreground'}`}
    >
      {feedback.kind === 'success' && <CheckCircle2 aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />}
      <span>{feedback.message}</span>
    </p>
  )
}

function LaboratoryEditor({ laboratory, organizationId, organizationName, clinics, onSaved, onCancel }: {
  laboratory: Laboratory | null
  organizationId: Id<'organizations'>
  organizationName: string
  clinics: ClinicOption[]
  onSaved: (id: Laboratory['_id']) => void
  onCancel: () => void
}) {
  const save = useMutation(api.externalLabs.saveLaboratory)
  const [fields, setFields] = useState({
    name: laboratory?.name ?? '', address: laboratory?.address ?? '',
    email: laboratory?.email ?? '', phone: laboratory?.phone ?? '',
    vat_number: laboratory?.vat_number ?? '', notes: laboratory?.notes ?? '',
  })
  const [saving, setSaving] = useState(false)
  const [clinicScope, setClinicScope] = useState<'all' | 'selected'>(laboratory?.clinic_ids ? 'selected' : 'all')
  const [clinicIds, setClinicIds] = useState<Id<'companies'>[]>(laboratory?.clinic_ids ?? [])
  const [error, setError] = useState<Feedback | null>(null)
  const mounted = useRef(true)
  const savingRef = useRef(false)
  useEffect(() => { mounted.current = true; return () => { mounted.current = false } }, [])
  const update = (key: keyof typeof fields, value: string) => setFields((previous) => ({ ...previous, [key]: value }))

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (savingRef.current) return
    setError(null)
    if (!fields.name.trim()) {
      setError({ kind: 'error', message: 'Inserisci il nome del laboratorio.' })
      return
    }
    if (fields.name.normalize('NFKD').replace(/[^a-z0-9]/gi, '').toLowerCase().includes('primolab')) {
      setError({ kind: 'error', message: 'PrimoLab ha già un listino dedicato. Inserisci il nome di un altro laboratorio.' })
      return
    }
    const selectedClinicIds = clinicIds.filter((id) => clinics.some((clinic) => clinic._id === id))
    if (clinicScope === 'selected' && selectedClinicIds.length === 0) {
      setError({ kind: 'error', message: 'Seleziona almeno una clinica oppure consenti l’utilizzo a tutte le cliniche della società.' })
      return
    }
    savingRef.current = true
    setSaving(true)
    try {
      const id = await save({ ...fields, organization_id: organizationId, clinic_ids: clinicScope === 'selected' ? selectedClinicIds : undefined, name: fields.name.trim(), ...(laboratory ? { id: laboratory._id } : {}) })
      if (mounted.current) onSaved(id)
    } catch (failure) {
      if (mounted.current) setError({ kind: 'error', message: errorMessage(failure) })
    } finally {
      savingRef.current = false
      if (mounted.current) setSaving(false)
    }
  }

  return (
    <form onSubmit={submit} className="rounded-lg border bg-muted/20 p-4 sm:p-6" aria-labelledby="external-laboratory-editor-title">
      <h2 id="external-laboratory-editor-title" className="text-lg font-semibold">{laboratory ? 'Modifica laboratorio' : 'Nuovo laboratorio'}</h2>
      <p className="mt-1 break-words text-sm text-muted-foreground">Società: {organizationName}. Il nome è obbligatorio.</p>
      <fieldset disabled={saving} className="mt-5 space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="flex flex-col gap-1.5 text-sm font-medium sm:col-span-2" htmlFor="external-laboratory-name">
            <span>Nome laboratorio *</span>
            <Input autoFocus id="external-laboratory-name" required maxLength={200} value={fields.name} onChange={(event) => update('name', event.target.value)} />
          </label>
          <label className="flex flex-col gap-1.5 text-sm font-medium sm:col-span-2" htmlFor="external-laboratory-address">
            <span>Indirizzo</span>
            <Input id="external-laboratory-address" autoComplete="street-address" maxLength={500} value={fields.address} onChange={(event) => update('address', event.target.value)} />
          </label>
          <label className="flex flex-col gap-1.5 text-sm font-medium" htmlFor="external-laboratory-email">
            <span>Email</span>
            <Input id="external-laboratory-email" type="email" autoComplete="email" maxLength={254} value={fields.email} onChange={(event) => update('email', event.target.value)} />
          </label>
          <label className="flex flex-col gap-1.5 text-sm font-medium" htmlFor="external-laboratory-phone">
            <span>Telefono</span>
            <Input id="external-laboratory-phone" type="tel" autoComplete="tel" maxLength={50} value={fields.phone} onChange={(event) => update('phone', event.target.value)} />
          </label>
          <label className="flex flex-col gap-1.5 text-sm font-medium" htmlFor="external-laboratory-vat">
            <span>Partita IVA</span>
            <Input id="external-laboratory-vat" maxLength={40} value={fields.vat_number} onChange={(event) => update('vat_number', event.target.value)} />
          </label>
          <label className="flex flex-col gap-1.5 text-sm font-medium sm:col-span-2" htmlFor="external-laboratory-notes">
            <span>Note anagrafiche</span>
            <textarea id="external-laboratory-notes" rows={3} className={fieldClass} maxLength={4000} value={fields.notes} onChange={(event) => update('notes', event.target.value)} />
          </label>
        </div>
        <fieldset className="space-y-3 border-t pt-4" aria-describedby="external-laboratory-clinics-help">
          <legend className="px-1 text-sm font-semibold">Cliniche abilitate</legend>
          <p id="external-laboratory-clinics-help" className="text-sm text-muted-foreground">Il laboratorio e il suo listino saranno disponibili nelle nuove prescrizioni delle cliniche abilitate.</p>
          <label className="flex min-h-10 cursor-pointer items-start gap-2 text-sm">
            <input type="radio" name="external-laboratory-clinic-scope" value="all" checked={clinicScope === 'all'} onChange={() => setClinicScope('all')} className="mt-0.5 h-4 w-4 shrink-0 accent-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring" />
            <span>Tutte le cliniche della società<span className="mt-1 block text-muted-foreground">Incluse quelle che saranno aggiunte in futuro.</span></span>
          </label>
          <label className="flex min-h-10 cursor-pointer items-center gap-2 text-sm">
            <input type="radio" name="external-laboratory-clinic-scope" value="selected" checked={clinicScope === 'selected'} onChange={() => setClinicScope('selected')} className="h-4 w-4 accent-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring" />
            Solo le cliniche selezionate
          </label>
          {clinicScope === 'selected' && <div className="space-y-2 pl-6">
            {clinics.length === 0 ? <p className="text-sm text-muted-foreground">Questa società non ha cliniche disponibili. Scegli tutte le cliniche oppure associa prima una clinica alla società.</p> : <>
              <p className="text-sm text-muted-foreground">Seleziona almeno una clinica.</p>
              <div className="grid gap-x-6 sm:grid-cols-2">
                {clinics.map((clinic) => <label key={clinic._id} className="flex min-h-10 cursor-pointer items-center gap-2 text-sm">
                  <input type="checkbox" checked={clinicIds.includes(clinic._id)} onChange={(event) => setClinicIds((previous) => event.target.checked ? [...previous, clinic._id] : previous.filter((id) => id !== clinic._id))} className="h-4 w-4 shrink-0 accent-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring" />
                  <span className="break-words">{clinic.nome}</span>
                </label>)}
              </div>
            </>}
          </div>}
          {laboratory && <p className="text-sm text-muted-foreground">Le prescrizioni già create restano consultabili nella rispettiva clinica.</p>}
        </fieldset>
        <FeedbackMessage feedback={error} />
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button type="submit" className="text-gray-950">{saving && <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" />}{saving ? 'Salvataggio…' : 'Salva laboratorio'}</Button>
          <Button type="button" variant="outline" onClick={onCancel}>Annulla</Button>
        </div>
      </fieldset>
    </form>
  )
}

function PriceItemEditor({ item, laboratory, organizationId, onSaved, onCancel }: {
  item: PriceItem | null
  laboratory: Laboratory
  organizationId: Id<'organizations'>
  onSaved: () => void
  onCancel: () => void
}) {
  const save = useMutation(api.externalLabs.savePriceItem)
  const [name, setName] = useState(item?.name ?? '')
  const [description, setDescription] = useState(item?.description ?? '')
  const [unitCost, setUnitCost] = useState(item?.unit_cost?.toFixed(2).replace('.', ',') ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<Feedback | null>(null)
  const mounted = useRef(true)
  const savingRef = useRef(false)
  useEffect(() => { mounted.current = true; return () => { mounted.current = false } }, [])

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (savingRef.current) return
    setError(null)
    const value = unitCost.trim()
    if (!name.trim() || !/^\d+(?:[.,]\d{1,2})?$/.test(value) || !Number.isFinite(Number(value.replace(',', '.'))) || Number(value.replace(',', '.')) > 1_000_000) {
      setError({ kind: 'error', message: 'Inserisci un nome e un costo compreso tra 0 e 1.000.000 euro, con al massimo due decimali.' })
      return
    }
    savingRef.current = true
    setSaving(true)
    try {
      await save({
        ...(item ? { id: item._id } : {}), laboratory_id: laboratory._id, organization_id: organizationId,
        name: name.trim(), description: description.trim(), unit_cost: Number(value.replace(',', '.')),
      })
      if (mounted.current) onSaved()
    } catch (failure) {
      if (mounted.current) setError({ kind: 'error', message: errorMessage(failure) })
    } finally {
      savingRef.current = false
      if (mounted.current) setSaving(false)
    }
  }

  return (
    <form onSubmit={submit} className="rounded-lg border bg-muted/20 p-4 sm:p-6" aria-labelledby="external-price-editor-title">
      <h2 id="external-price-editor-title" className="text-lg font-semibold">{item ? 'Modifica voce di listino' : 'Nuova voce di listino'}</h2>
      <p className="mt-1 break-words text-sm text-muted-foreground">Laboratorio: {laboratory.name}. Nome e costo sono obbligatori.</p>
      <fieldset disabled={saving} className="mt-5 space-y-4">
        <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_180px]">
          <label htmlFor="external-price-name" className="flex flex-col gap-1.5 text-sm font-medium">
            <span>Prestazione *</span>
            <Input autoFocus id="external-price-name" required maxLength={200} value={name} onChange={(event) => setName(event.target.value)} />
          </label>
          <label htmlFor="external-price-cost" className="flex flex-col gap-1.5 text-sm font-medium">
            <span>Costo unitario (€) *</span>
            <Input id="external-price-cost" inputMode="decimal" required maxLength={14} placeholder="0,00" value={unitCost} onChange={(event) => setUnitCost(event.target.value)} />
          </label>
          <label htmlFor="external-price-description" className="flex flex-col gap-1.5 text-sm font-medium sm:col-span-2">
            <span>Descrizione</span>
            <textarea id="external-price-description" rows={3} className={fieldClass} maxLength={2000} value={description} onChange={(event) => setDescription(event.target.value)} />
          </label>
        </div>
        <FeedbackMessage feedback={error} />
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button type="submit" className="text-gray-950">{saving && <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" />}{saving ? 'Salvataggio…' : 'Salva voce'}</Button>
          <Button type="button" variant="outline" onClick={onCancel}>Annulla</Button>
        </div>
      </fieldset>
    </form>
  )
}

function ExternalCatalogWorkspace({ organizationId, organizationName = '', clinics = [], onBusyChange }: {
  organizationId?: Id<'organizations'>
  organizationName?: string
  clinics?: ClinicOption[]
  onBusyChange: (busy: boolean) => void
}) {
  const { profile } = useAuth()
  const { hasPermission } = usePermissions()
  const canManage = Boolean(organizationId) && hasPermission('manage_external_laboratories') && Boolean(profile && ['clinic_owner', 'staff', 'amministratore'].includes(profile.ruolo))
  const canViewPrices = hasPermission('view_external_prices')
  const canManagePrices = canManage && canViewPrices
  const [showArchived, setShowArchived] = useState(false)
  const [selectedId, setSelectedId] = useState<Laboratory['_id'] | ''>('')
  const [search, setSearch] = useState('')
  const [laboratoryEditor, setLaboratoryEditor] = useState<{ laboratory: Laboratory | null } | null>(null)
  const [priceEditor, setPriceEditor] = useState<{ item: PriceItem | null } | null>(null)
  const [archiveTarget, setArchiveTarget] = useState<ArchiveTarget | null>(null)
  const [pending, setPending] = useState(false)
  const [feedback, setFeedback] = useState<Feedback | null>(null)
  const laboratories = useQuery(api.externalLabs.listLaboratories, organizationId || profile?.company_id ? { ...(organizationId ? { organization_id: organizationId } : {}), include_archived: showArchived } : 'skip')
  const selected = laboratories?.find((laboratory) => laboratory._id === selectedId) ?? laboratories?.find((laboratory) => !laboratory.archived) ?? laboratories?.[0]
  const prices = useQuery(api.externalLabs.listPriceItems, selected ? { laboratory_id: selected._id, ...(organizationId ? { organization_id: organizationId } : {}), include_archived: showArchived } : 'skip')
  const archiveLaboratory = useMutation(api.externalLabs.archiveLaboratory)
  const archivePriceItem = useMutation(api.externalLabs.archivePriceItem)
  const editing = Boolean((canManage && laboratoryEditor) || (canManagePrices && priceEditor))
  const filteredPrices = prices?.filter((item) => `${item.name} ${item.description ?? ''}`.toLocaleLowerCase('it').includes(search.trim().toLocaleLowerCase('it')))
  const mounted = useRef(true)
  const pendingRef = useRef(false)
  useEffect(() => { mounted.current = true; return () => { mounted.current = false } }, [])
  useEffect(() => {
    onBusyChange(editing || pending || Boolean(archiveTarget))
    return () => onBusyChange(false)
  }, [editing, pending, archiveTarget, onBusyChange])

  useEffect(() => {
    if (!canManage) {
      setLaboratoryEditor(null)
      setArchiveTarget(null)
    }
    if (!canManagePrices) setPriceEditor(null)
  }, [canManage, canManagePrices])

  async function changeArchive(target: ArchiveTarget) {
    if (pendingRef.current || !organizationId) return
    pendingRef.current = true
    setPending(true)
    setFeedback(null)
    try {
      if (target.kind === 'laboratory') await archiveLaboratory({ id: target.item._id, organization_id: organizationId, archived: !target.item.archived })
      else await archivePriceItem({ id: target.item._id, organization_id: organizationId, archived: !target.item.archived })
      if (!mounted.current) return
      setArchiveTarget(null)
      const subject = target.kind === 'laboratory' ? 'Laboratorio' : 'Voce'
      const action = target.item.archived ? 'ripristinat' : 'archiviat'
      setFeedback({ kind: 'success', message: `${subject} ${action}${target.kind === 'laboratory' ? 'o' : 'a'}.` })
    } catch (failure) {
      if (mounted.current) setFeedback({ kind: 'error', message: errorMessage(failure) })
    } finally {
      pendingRef.current = false
      if (mounted.current) setPending(false)
    }
  }

  if (!organizationId && !profile?.company_id) return <p className="py-6 text-muted-foreground">Seleziona una clinica per consultare i laboratori esterni.</p>

  return (
    <div className="space-y-6">
      {canManage && <div className="flex justify-end"><Button variant="outline" disabled={editing || pending || Boolean(archiveTarget)} onClick={() => { setFeedback(null); setLaboratoryEditor({ laboratory: null }) }}><Plus aria-hidden="true" className="mr-2 h-4 w-4" />Nuovo laboratorio</Button></div>}

      <FeedbackMessage feedback={feedback} />
      {pending && <p role="status" className="text-sm text-muted-foreground">Aggiornamento archivio…</p>}

      {canManage && organizationId && laboratoryEditor && <LaboratoryEditor laboratory={laboratoryEditor.laboratory} organizationId={organizationId} organizationName={organizationName} clinics={clinics} onCancel={() => setLaboratoryEditor(null)} onSaved={(id) => { setSelectedId(id); setSearch(''); setLaboratoryEditor(null); setFeedback({ kind: 'success', message: 'Anagrafica laboratorio salvata.' }) }} />}

      <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
        <label htmlFor="external-catalog-laboratory" className="min-w-0 flex-1 flex flex-col gap-1.5 text-sm font-medium">
          <span>Laboratorio</span>
          <select id="external-catalog-laboratory" className={`${fieldClass} h-10`} value={selected?._id ?? ''} disabled={editing || pending || Boolean(archiveTarget) || !laboratories?.length} onChange={(event) => { const laboratory = laboratories?.find((item) => item._id === event.target.value); setSelectedId(laboratory?._id ?? ''); setSearch(''); setFeedback(null) }}>
            {!laboratories?.length && <option value="">{laboratories === undefined ? 'Caricamento laboratori…' : 'Nessun laboratorio disponibile'}</option>}
            {laboratories?.map((laboratory) => <option key={laboratory._id} value={laboratory._id}>{laboratory.name}{laboratory.archived ? ' · Archiviato' : ''}</option>)}
          </select>
        </label>
        <label className="flex min-h-10 cursor-pointer items-center gap-2 text-sm">
          <input type="checkbox" checked={showArchived} disabled={editing || pending || Boolean(archiveTarget)} onChange={(event) => setShowArchived(event.target.checked)} className="h-4 w-4 accent-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring" />
          Mostra archiviati
        </label>
      </div>

      {laboratories === undefined && <div role="status" aria-busy="true" className="space-y-3 py-4"><p className="text-sm text-muted-foreground">Caricamento delle anagrafiche…</p><div className="h-16 rounded-md bg-muted" /><div className="h-12 rounded-md bg-muted" /></div>}
      {laboratories?.length === 0 && <div className="border-y py-8"><h2 className="font-semibold">{showArchived ? 'Nessun laboratorio esterno' : 'Nessun laboratorio attivo'}</h2><p className="mt-2 max-w-prose text-sm text-muted-foreground">{canManage ? 'Crea l’anagrafica del primo laboratorio della società, scegli le cliniche abilitate e aggiungi le prestazioni al suo listino. PrimoLab resta nella scheda dedicata.' : 'Il titolare o il personale autorizzato può configurare i laboratori della società e abilitarli per questa clinica.'}</p></div>}

      {selected && <>
        <div className="flex flex-col justify-between gap-4 border-y py-4 sm:flex-row">
          <div className="min-w-0 space-y-1 text-sm">
            <h2 className="break-words text-base font-semibold">{selected.name}{selected.archived && <span className="ml-2 text-sm font-normal text-muted-foreground">Archiviato</span>}</h2>
            {canManage && <p className="max-w-prose break-words text-muted-foreground">Cliniche abilitate: {selected.clinic_ids ? selected.clinic_ids.map((id) => clinics.find((clinic) => clinic._id === id)?.nome ?? 'Clinica non più disponibile').join(', ') : 'tutte le cliniche della società, anche quelle future'}.</p>}
            {selected.address && <p className="break-words text-muted-foreground">{selected.address}</p>}
            {selected.email && <p className="break-words">Email: {selected.email}</p>}
            {selected.phone && <p className="break-words">Telefono: {selected.phone}</p>}
            {selected.vat_number && <p className="break-words">P. IVA: {selected.vat_number}</p>}
            {selected.notes && <p className="max-w-prose whitespace-pre-wrap break-words pt-2 text-muted-foreground">{selected.notes}</p>}
          </div>
          {canManage && <div className="flex shrink-0 flex-wrap gap-2 sm:items-start">
            {!selected.archived && <Button variant="outline" size="sm" disabled={editing || pending || Boolean(archiveTarget)} onClick={() => { setFeedback(null); setLaboratoryEditor({ laboratory: selected }) }}><Pencil aria-hidden="true" className="mr-2 h-4 w-4" />Modifica anagrafica</Button>}
            <Button variant="ghost" size="sm" disabled={editing || pending || Boolean(archiveTarget)} onClick={() => selected.archived ? void changeArchive({ kind: 'laboratory', item: selected }) : setArchiveTarget({ kind: 'laboratory', item: selected })}>{selected.archived ? <ArchiveRestore aria-hidden="true" className="mr-2 h-4 w-4" /> : <Archive aria-hidden="true" className="mr-2 h-4 w-4" />}{selected.archived ? 'Ripristina' : 'Archivia'}</Button>
          </div>}
        </div>

        {canManage && archiveTarget && <div className="space-y-3 rounded-lg border bg-muted/30 p-4" role="group" aria-labelledby="external-archive-title">
          <h2 id="external-archive-title" className="break-words font-semibold">Archiviare “{archiveTarget.item.name}”?</h2>
          <p className="max-w-prose text-sm text-muted-foreground">{archiveTarget.kind === 'laboratory' ? 'Il laboratorio e il suo listino non saranno disponibili nelle nuove prescrizioni. Le prescrizioni esistenti restano consultabili.' : 'La prestazione non sarà disponibile nelle nuove prescrizioni. Le prescrizioni esistenti conservano il costo salvato.'} Puoi ripristinar{archiveTarget.kind === 'laboratory' ? 'lo' : 'la'} mostrando gli archiviati.</p>
          <div className="flex flex-col gap-2 sm:flex-row"><Button autoFocus className="text-gray-950" disabled={pending} onClick={() => void changeArchive(archiveTarget)}>{pending ? 'Archiviazione…' : 'Conferma archiviazione'}</Button><Button variant="outline" disabled={pending} onClick={() => setArchiveTarget(null)}>Annulla</Button></div>
        </div>}

        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
          <div><h2 className="text-lg font-semibold">Prestazioni</h2>{!canViewPrices && <p className="mt-1 text-sm text-muted-foreground">I costi non sono visibili al tuo ruolo.</p>}</div>
          {canManagePrices && !selected.archived && <Button className="text-gray-950" disabled={editing || pending || Boolean(archiveTarget)} onClick={() => { setFeedback(null); setPriceEditor({ item: null }) }}><Plus aria-hidden="true" className="mr-2 h-4 w-4" />Nuova prestazione</Button>}
        </div>
        {selected.archived && <p className="text-sm text-muted-foreground">Ripristina il laboratorio per aggiungere o modificare prestazioni.</p>}
        {canManagePrices && organizationId && priceEditor && <PriceItemEditor item={priceEditor.item} laboratory={selected} organizationId={organizationId} onCancel={() => setPriceEditor(null)} onSaved={() => { setPriceEditor(null); setFeedback({ kind: 'success', message: 'Voce di listino salvata. Il nuovo costo si applica alle nuove prescrizioni delle cliniche abilitate.' }) }} />}

        <div className="max-w-lg">
          <label htmlFor="external-catalog-search" className="mb-1.5 block text-sm font-medium">Cerca prestazione</label>
          <div className="relative"><Search aria-hidden="true" className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" /><Input id="external-catalog-search" type="search" className="pl-9" placeholder="Nome o descrizione" value={search} onChange={(event) => setSearch(event.target.value)} /></div>
        </div>

        {prices === undefined ? <div role="status" aria-busy="true" className="space-y-3"><p className="text-sm text-muted-foreground">Caricamento del listino…</p><div className="h-20 rounded-md bg-muted" /><div className="h-20 rounded-md bg-muted" /></div> : filteredPrices?.length === 0 ? <div className="border-y py-8"><h3 className="font-medium">{search.trim() ? 'Nessuna prestazione trovata' : 'Il listino non contiene prestazioni'}</h3><p className="mt-2 text-sm text-muted-foreground">{search.trim() ? 'Prova un altro nome o cancella la ricerca.' : canManagePrices && !selected.archived ? 'Aggiungi una prestazione con nome e costo unitario per usarla nelle prescrizioni.' : 'Le prestazioni aggiunte al laboratorio compariranno qui.'}</p>{search.trim() && <Button variant="outline" size="sm" className="mt-3" onClick={() => setSearch('')}>Cancella ricerca</Button>}</div> : <ul className="divide-y border-y" aria-label="Prestazioni del laboratorio">
          {filteredPrices?.map((item) => <li key={item._id} className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:gap-6">
            <div className="min-w-0 flex-1"><p className="break-words font-medium">{item.name}{item.archived && <span className="ml-2 text-sm font-normal text-muted-foreground">Archiviata</span>}</p>{item.description && <p className="mt-1 max-w-prose whitespace-pre-wrap break-words text-sm text-muted-foreground">{item.description}</p>}</div>
            <div className="flex flex-wrap items-center justify-between gap-3 sm:justify-end">
              {canViewPrices && <p className="whitespace-nowrap font-medium tabular-nums"><span className="sr-only">Costo unitario: </span>{item.unit_cost !== undefined ? formatCurrency(item.unit_cost) : 'Costo non disponibile'}</p>}
              {canManage && !selected.archived && <div className="flex gap-1">
                {!item.archived && canManagePrices && <Button variant="ghost" size="sm" disabled={editing || pending || Boolean(archiveTarget)} aria-label={`Modifica ${item.name}`} onClick={() => { setFeedback(null); setPriceEditor({ item }) }}>Modifica</Button>}
                <Button variant="ghost" size="sm" disabled={editing || pending || Boolean(archiveTarget)} aria-label={`${item.archived ? 'Ripristina' : 'Archivia'} ${item.name}`} onClick={() => item.archived ? void changeArchive({ kind: 'price', item }) : setArchiveTarget({ kind: 'price', item })}>{item.archived ? 'Ripristina' : 'Archivia'}</Button>
              </div>}
            </div>
          </li>)}
        </ul>}
      </>}
    </div>
  )
}

function ExternalCatalogContent() {
  const { profile } = useAuth()
  const { hasPermission } = usePermissions()
  const canManage = hasPermission('manage_external_laboratories') && Boolean(profile && ['clinic_owner', 'staff', 'amministratore'].includes(profile.ruolo))
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<Id<'organizations'> | ''>('')
  const [busy, setBusy] = useState(false)
  const initialContext = useQuery(api.externalLabs.getCatalogContext, canManage ? {} : 'skip')
  const selectedContext = useQuery(api.externalLabs.getCatalogContext, canManage && selectedOrganizationId ? { organization_id: selectedOrganizationId } : 'skip')
  const context = selectedOrganizationId ? selectedContext : initialContext
  const organizationId = context?.organization_id ?? undefined
  const organizationName = context?.organizations.find((organization) => organization._id === organizationId)?.nome ?? ''

  useEffect(() => {
    if (!canManage) {
      setSelectedOrganizationId('')
      setBusy(false)
    }
  }, [canManage])

  return (
    <section className="space-y-6" aria-labelledby="external-catalog-title">
      <div>
        <h1 id="external-catalog-title" className="text-xl font-semibold tracking-tight">Listini dei laboratori esterni</h1>
        <p className="mt-1 max-w-prose text-sm text-muted-foreground">{canManage ? 'Gestisci laboratori e listini della società. Per ogni laboratorio puoi abilitare tutte le cliniche oppure solo quelle selezionate.' : 'Consulta i laboratori e le prestazioni disponibili per la clinica attiva.'}</p>
      </div>
      {canManage && <div className="max-w-xl space-y-2">
        <label htmlFor="external-catalog-organization" className="flex flex-col gap-1.5 text-sm font-medium">
          <span>Società</span>
          <select id="external-catalog-organization" className={`${fieldClass} h-10`} value={selectedOrganizationId || initialContext?.organization_id || ''} disabled={busy || !initialContext?.organizations.length} onChange={(event) => setSelectedOrganizationId(event.target.value as Id<'organizations'>)}>
            {!initialContext?.organizations.length && <option value="">{initialContext === undefined ? 'Caricamento società…' : 'Nessuna società disponibile'}</option>}
            {initialContext?.organizations.map((organization) => <option key={organization._id} value={organization._id}>{organization.nome}</option>)}
          </select>
        </label>
        {busy && <p className="text-sm text-muted-foreground">Completa o annulla l’operazione in corso prima di cambiare società.</p>}
      </div>}
      {canManage ? context === undefined ? <p role="status" aria-busy="true" className="py-4 text-sm text-muted-foreground">Caricamento della società e delle cliniche…</p> : !organizationId ? <div className="border-y py-6"><h2 className="font-semibold">Nessuna società disponibile</h2><p className="mt-2 max-w-prose text-sm text-muted-foreground">Per configurare laboratori e listini serve una società. Un amministratore può crearla e collegarvi le cliniche.</p></div> : <ExternalCatalogWorkspace key={`organization:${organizationId}`} organizationId={organizationId} organizationName={organizationName} clinics={context.clinics} onBusyChange={setBusy} /> : <ExternalCatalogWorkspace key={`clinic:${profile?.company_id ?? ''}`} onBusyChange={setBusy} />}
    </section>
  )
}

export function ExternalCatalog() {
  return (
    <ErrorBoundary fallback={<div role="alert" className="space-y-3 py-6"><h2 className="font-semibold">Listini esterni non disponibili</h2><p className="text-sm text-muted-foreground">Non è stato possibile caricare le anagrafiche e le prestazioni. Ricarica la pagina per riprovare.</p><Button variant="outline" onClick={() => window.location.reload()}>Ricarica pagina</Button></div>}>
      <ExternalCatalogContent />
    </ErrorBoundary>
  )
}
